// Copyright 2020 Drivetrain Hub LLC
// Functions for calculating discretized data points from parametric microgeometry definitions.
// Microgeometry is computed in normalized coordinates, i.e. facewidth and roll angles vary from 0-1.
// Functions use (+) sign convention for the amount of modification, e.g. (+) crown ==> (+) modification.

import math from "./math";

// region HELPERS

function lineFcn(slope, yIntercept, x) {
  return slope * x + yIntercept;
}

/**
 * Compute parabola focal length. Argument requirements:
 *    1. xArbitrary != vertexX
 *    2. yArbitrary != vertexY
 * @param {number} vertexX - Parabola vertex x-coordinate.
 * @param {number} vertexY - Parabola vertex y-coordinate.
 * @param {number} xArbitrary - Arbitrary x-coordinate on parabolic curve.
 * @param {number} yArbitrary - Arbitrary y-coordinate on parabolic curve.
 * @return {number} Parabola focal length.
 */
function parabolaFocalLength(vertexX, vertexY, xArbitrary, yArbitrary) {
  return -((vertexX - xArbitrary) ** 2) / (4 * (vertexY - yArbitrary));
}

/**
 * Compute parabola function in x-y plane.
 * @param {number} vertexX - Parabola vertex x-coordinate.
 * @param {number} vertexY - Parabola vertex y-coordinate.
 * @param {number} focalLength - Parabolic focal length.
 * @param {number} x - x-coordinate.
 * @return {number} Value of parabola function.
 */
function parabolaFcn(vertexX, vertexY, focalLength, x) {
  return 1 / (4 * focalLength) * (x - vertexX) ** 2 + vertexY;
}

/**
 * Compute parabola function in x-y plane. Argument requirements:
 *    1. xArbitrary != vertexX
 *    2. yArbitrary != vertexY
 * @param {number} vertexX - Parabola vertex x-coordinate.
 * @param {number} vertexY - Parabola vertex y-coordinate.
 * @param {number} xArbitrary - Arbitrary x-coordinate on parabolic curve.
 * @param {number} yArbitrary - Arbitrary y-coordinate on parabolic curve.
 * @param {[]} xArr - Array of x-coordinates.
 * @return {[]} Array of parabola y-coordinates.
 */
function parabolaCoord(vertexX, vertexY, xArbitrary, yArbitrary, xArr) {
  const focalLength = parabolaFocalLength(vertexX, vertexY, xArbitrary, yArbitrary);
  return xArr.map(x => parabolaFcn(vertexX, vertexY, focalLength, x));
}

// endregion

// region PROFILE MODIFICATIONS

/**
 * Compute profile slope coordinates.  Reference _calc_profile_slope_curve.
 * Sign convention:
 *    1. Positive (+) profile slope increases relief as a function roll angle.
 *    2. With relief modifications being positive, this convention matches that of a standard line equation.
 * @param {number} val - Amount of profile slope.
 * @param {number} evalLimitLower - Lower evaluation limit of profile slope, normalized.
 * @param {number} evalLimitUpper - Upper evaluation limit of profile slope, normalized.
 * @param {[]} xArr - Array of normalized profile coordinates.
 * @return {[]} Profile slope modification.
 */
function profileSlopeCoord(val, evalLimitLower, evalLimitUpper, xArr) {
  // PLACEHOLDER {DRY} equations are the same as lead slope
  // calculate slope of line eqn: y = m*x + b
  const evalLimitRange = evalLimitUpper - evalLimitLower;
  const slope = val / evalLimitRange;

  // calculate y-intercept of line eqn: b = y - m*x
  // slope > 0 ==> x-intercept @ (start of profile, 0)
  // slope < 0 ==> x-intercept @ (end of profile, 0)
  const yIntercept = slope >= 0 ? 0 : -slope;

  return xArr.map(x => lineFcn(slope, yIntercept, x));
}

/**
 * Compute profile crown coordinates.  Reference _calc_profile_crown_curve.
 * Evaluation limits:
 *    1. The magnitude of profile crown applies at the profile evaluation limits.
 *    2. The profile crown is centered between the evaluation limits, where its amplitude is zero.
 * @param {number} val - Amount of profile crown.
 * @param {number} evalLimitLower - Lower evaluation limit of profile crown.
 * @param {number} evalLimitUpper - Upper evaluation limit of profile crown.
 * @param {[]} xArr - Array of normalized profile coordinates.
 * @return {[]} Profile crown modification.
 */
function profileCrownCoord(val, evalLimitLower, evalLimitUpper, xArr) {
  // PLACEHOLDER {DRY} equations are the same as lead crown
  // calculate center of crown
  const evalLimitRange = evalLimitUpper - evalLimitLower;
  const evalLimitMedian = evalLimitLower + evalLimitRange / 2;

  // parabola params
  const xArbitrary = evalLimitLower;
  const yArbitrary = val;
  const vertexX = evalLimitMedian;
  const vertexY = 0;

  return parabolaCoord(vertexX, vertexY, xArbitrary, yArbitrary, xArr);
}

/**
 * Compute profile root relief coordinates.
 * Reference _calc_root_relief_linear_curve and _calc_root_relief_parabolic_curve.
 * The magnitude of relief applies between the start of involute profile and start of relief.
 * @param {number} val - Amount of relief.
 * @param {number} start - Start of relief.
 * @param {string} type - Type of relief.
 * @param {[]} xArr - Array of normalized profile coordinates.
 * @return {[]} Profile root relief modification.
 */
function rootReliefCoord(val, start, type, xArr) {
  // PLACEHOLDER {DRY} equations are the same as bottom end relief
  switch (type.toLowerCase()) {
    case 'linear': {
      // y = m*x + b, where b is the y-intercept
      // x-intercept @ (start, 0)
      const slope = -val / start;
      const yIntercept = -slope * start;

      // compute relief coordinates
      return xArr.map(x => {
        return x < start ? lineFcn(slope, yIntercept, x) : 0;
      });
    }
    case 'parabolic': {
      const xArbitrary = 0;
      const yArbitrary = val;
      const vertexX = start;
      const vertexY = 0;
      const focalLength = parabolaFocalLength(vertexX, vertexY, xArbitrary, yArbitrary);

      // compute relief coordinates
      return xArr.map(x => {
        return x < start ? parabolaFcn(vertexX, vertexY, focalLength, x) : 0;
      });
    }
  }
}

/**
 * Compute profile tip relief coordinates.
 * Reference _calc_tip_relief_linear_curve and _calc_tip_relief_parabolic_curve.
 * The magnitude of relief applies between the start of relief and end of profile.
 * @param {number} val - Amount of relief.
 * @param {number} start - Start of relief.
 * @param {string} type - Type of relief.
 * @param {[]} xArr - Array of normalized profile coordinates.
 * @return {[]} Profile tip relief modification.
 */
function tipReliefCoord(val, start, type, xArr) {
  // PLACEHOLDER {DRY} equations are the same as top end relief
  switch (type.toLowerCase()) {
    case 'linear': {
      // y = m*x + b, where b is the y-intercept
      // x-intercept @ (start, 0)
      const slope = val / (1 - start);
      const yIntercept = -slope * start;

      // compute relief coordinates
      return xArr.map(x => {
        return x > start ? lineFcn(slope, yIntercept, x) : 0;
      });
    }
    case 'parabolic': {
      const xArbitrary = 1;
      const yArbitrary = val;
      const vertexX = start;
      const vertexY = 0;
      const focalLength = parabolaFocalLength(vertexX, vertexY, xArbitrary, yArbitrary);

      // compute relief coordinates
      return xArr.map(x => {
        return x > start ? parabolaFcn(vertexX, vertexY, focalLength, x) : 0;
      });
    }
  }
}

/**
 * Compute the summation of profile modifications.
 * @return {[]} Values of profile modification.
 */
function profileReliefCoord(
  {slope, crown, evalLimitLower, evalLimitUpper,
    rootRelief, rootReliefStart, rootReliefType,
    tipRelief, tipReliefStart, tipReliefType}, n) {

  // array of normalized profile coordinates
  const xArr = math.linspace(0, 1, n);

  // compute profile modifications
  const slopeModification = profileSlopeCoord(slope, evalLimitLower, evalLimitUpper, xArr);
  const crownModification = profileCrownCoord(crown, evalLimitLower, evalLimitUpper, xArr);
  const rootModification = rootReliefCoord(rootRelief, rootReliefStart, rootReliefType, xArr);
  const tipModification = tipReliefCoord(tipRelief, tipReliefStart, tipReliefType, xArr);

  // DEV ONLY
  // console.log('PROFILE:');
  // console.log('slopeModification', slopeModification);
  // console.log('crownModification', crownModification);
  // console.log('rootModification', rootModification);
  // console.log('tipModification', tipModification);

  // sum profile modifications
  let profileModification = [];
  for (let i=0; i < xArr.length; i++) {
    profileModification.push(slopeModification[i] + crownModification[i] + rootModification[i] + tipModification[i]);
  }

  return profileModification;
}

// endregion

// region LEAD MODIFICATIONS

/**
 * Compute lead slope coordinates.  Reference _calc_lead_slope_curve.
 * Sign convention:
 *    1. Positive (+) lead slope increases relief as a function effective facewidth.
 *    2. With relief modifications being positive, this convention matches that of a standard line equation.
 * @param {number} val - Amount of lead slope.
 * @param {number} evalLimitLower - Lower evaluation limit of lead slope, normalized.
 * @param {number} evalLimitUpper - Upper evaluation limit of lead slope, normalized.
 * @param {[]} xArr - Array of normalized lead coordinates.
 * @return {[]} Lead slope modification.
 */
function leadSlopeCoord(val, evalLimitLower, evalLimitUpper, xArr) {
  // calculate slope of line eqn: y = m*x + b
  const evalLimitRange = evalLimitUpper - evalLimitLower;
  const slope = val / evalLimitRange;

  // calculate y-intercept of line eqn: b = y - m*x
  // slope > 0 ==> x-intercept @ (start of effective facewidth, 0)
  // slope < 0 ==> x-intercept @ (end of effective facewidth, 0)
  const yIntercept = slope >= 0 ? 0 : -slope;

  return xArr.map(x => lineFcn(slope, yIntercept, x));
}

/**
 * Compute lead crown coordinates.  Reference _calc_lead_crown_curve.
 * Evaluation limits:
 *    1. The magnitude of lead crown applies at the lead evaluation limits.
 *    2. The lead crown is centered between the evaluation limits, where its amplitude is zero.
 * @param {number} val - Amount of lead crown.
 * @param {number} evalLimitLower - Lower evaluation limit of lead crown.
 * @param {number} evalLimitUpper - Upper evaluation limit of lead crown.
 * @param {[]} xArr - Array of normalized lead coordinates.
 * @return {[]} Lead crown modification.
 */
function leadCrownCoord(val, evalLimitLower, evalLimitUpper, xArr) {
  // calculate center of crown
  const evalLimitRange = evalLimitUpper - evalLimitLower;
  const evalLimitMedian = evalLimitLower + evalLimitRange / 2;

  // parabola params
  const xArbitrary = evalLimitLower;
  const yArbitrary = val;
  const vertexX = evalLimitMedian;
  const vertexY = 0;

  return parabolaCoord(vertexX, vertexY, xArbitrary, yArbitrary, xArr);
}

/**
 * Compute lead bottom end relief coordinates.
 * Reference _calc_end_relief_linear_lower_curve and _calc_end_relief_parabolic_lower_curve.
 * The magnitude of relief applies between the start of facewidth and start of relief.
 * @param {number} val - Amount of relief.
 * @param {number} start - Start of relief.
 * @param {string} type - Type of relief.
 * @param {[]} xArr - Array of normalized lead coordinates.
 * @return {[]} Bottom relief lead modification.
 */
function bottomReliefCoord(val, start, type, xArr) {
  switch (type.toLowerCase()) {
    case 'linear': {
      // y = m*x + b, where b is the y-intercept
      // x-intercept @ (start, 0)
      const slope = -val / start;
      const yIntercept = -slope * start;

      // compute relief coordinates
      return xArr.map(x => {
        return x < start ? lineFcn(slope, yIntercept, x) : 0;
      });
    }
    case 'parabolic': {
      const xArbitrary = 0;
      const yArbitrary = val;
      const vertexX = start;
      const vertexY = 0;
      const focalLength = parabolaFocalLength(vertexX, vertexY, xArbitrary, yArbitrary);

      // compute relief coordinates
      return xArr.map(x => {
        return x < start ? parabolaFcn(vertexX, vertexY, focalLength, x) : 0;
      });
    }
  }
}

/**
 * Compute lead top end relief coordinates.
 * Reference _calc_end_relief_linear_upper_curve and _calc_end_relief_parabolic_upper_curve.
 * The magnitude of relief applies between the start of relief and end of facewidth.
 * @param {number} val - Amount of relief.
 * @param {number} start - Start of relief.
 * @param {string} type - Type of relief.
 * @param {[]} xArr - Array of normalized lead coordinates.
 * @return {[]} Top relief lead modification.
 */
function topReliefCoord(val, start, type, xArr) {
  switch (type.toLowerCase()) {
    case 'linear': {
      // y = m*x + b, where b is the y-intercept
      // x-intercept @ (start, 0)
      const slope = val / (1 - start);
      const yIntercept = -slope * start;

      // compute relief coordinates
      return xArr.map(x => {
        return x > start ? lineFcn(slope, yIntercept, x) : 0;
      });
    }
    case 'parabolic': {
      const xArbitrary = 1;
      const yArbitrary = val;
      const vertexX = start;
      const vertexY = 0;
      const focalLength = parabolaFocalLength(vertexX, vertexY, xArbitrary, yArbitrary);

      // compute relief coordinates
      return xArr.map(x => {
        return x > start ? parabolaFcn(vertexX, vertexY, focalLength, x) : 0;
      });
    }
  }
}

/**
 * Compute the summation of lead modifications.
 * @return {[]} Values of lead modification.
 */
function leadReliefCoord(
  {slope, crown, evalLimitLower, evalLimitUpper,
    bottomRelief, bottomReliefStart, bottomReliefType,
    topRelief, topReliefStart, topReliefType}, n) {

  // array of normalized lead coordinates
  const xArr = math.linspace(0, 1, n);

  // compute lead modifications
  const slopeModification = leadSlopeCoord(slope, evalLimitLower, evalLimitUpper, xArr);
  const crownModification = leadCrownCoord(crown, evalLimitLower, evalLimitUpper, xArr);
  const bottomModification = bottomReliefCoord(bottomRelief, bottomReliefStart, bottomReliefType, xArr);
  const topModification = topReliefCoord(topRelief, topReliefStart, topReliefType, xArr);

  // DEV ONLY
  // console.log('LEAD:');
  // console.log('slopeModification', slopeModification);
  // console.log('crownModification', crownModification);
  // console.log('bottomModification', bottomModification);
  // console.log('topModification', topModification);

  // sum lead modifications
  let leadModification = [];
  for (let i=0; i < xArr.length; i++) {
    leadModification.push(slopeModification[i] + crownModification[i] + bottomModification[i] + topModification[i]);
  }

  return leadModification;
}

// endregion

/**
 * Compute microgeometry modifications for the flank surface.
 * @param {slope, crown, evalLimitLower, evalLimitUpper,
    rootRelief, rootReliefStart, rootReliefType,
    tipRelief, tipReliefStart, tipReliefType} profile - Parametric lead modifications.
 * @param {slope, crown, evalLimitLower, evalLimitUpper,
    bottomRelief, bottomReliefStart, bottomReliefType,
    topRelief, topReliefStart, topReliefType} lead - Parametric profile modifications.
 * @param {number} nProfile - Discretization in profile direction.
 * @param {number} nLead - Discretization in lead direction.
 * @param {number} sign - Sign convention of modification values.
 * @return {[[]]} Flank surface modification.
 */
function flankReliefCoord(profile, lead, nProfile, nLead, sign=1) {
  // compute profile and lead modifications
  let profileModification = profileReliefCoord(profile, nProfile);
  let leadModification = leadReliefCoord(lead, nLead);

  // sum all flank modifications
  let flankModification = [];
  for (let i=0; i < nProfile; i++) {
    const pmod = profileModification[i];
    flankModification.push(leadModification.map(lmod => sign * (lmod + pmod)));
  }

  return flankModification;
}

export default {
  profileReliefCoord: profileReliefCoord,
  leadReliefCoord: leadReliefCoord,
  flankReliefCoord: flankReliefCoord,
}
